import React, { useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { format, differenceInDays, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, isSameDay, isSameWeek, isSameMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { UNIVERS_ALL, visitsWithDuration } from '../../constants/visitsUnivers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function VisitsDurationChart({ visits, stats, selectedUnivers = UNIVERS_ALL }) {
  // One rule for every duration in the module — see visitsWithDuration().
  // Derived once: the JSX below tests the same list for its empty state.
  const dated = useMemo(
    () => visitsWithDuration(visits, selectedUnivers).filter((visit) => visit.date),
    [visits, selectedUnivers]
  );

  // Données préparées pour les graphiques
  const chartData = useMemo(() => {

    // Si pas de visites avec durée, retourner des données vides
    if (dated.length === 0) {
      return {
        durationPerStore: {
          labels: [],
          datasets: [{ data: [] }]
        },
        durationTrend: {
          labels: [],
          datasets: [{ data: [] }]
        },
        durationHistogram: {
          labels: ['0-15', '16-30', '31-45', '46-60', '61-90', '91-120', '>120'],
          datasets: [{ data: [0, 0, 0, 0, 0, 0, 0] }]
        }
      };
    }

    // 1. Durée moyenne par enseigne
    const storeData = {};
    dated.forEach(visit => {
      if (!storeData[visit.shopSign]) {
        storeData[visit.shopSign] = { total: 0, count: 0 };
      }
      storeData[visit.shopSign].total += visit.durationMinutes;
      storeData[visit.shopSign].count += 1;
    });

    const storeLabels = Object.keys(storeData);
    const storeDurations = storeLabels.map(store =>
      Math.round(storeData[store].total / storeData[store].count)
    );

    // 2. Tendance des durées dans le temps
    // Trouver les dates min et max des visites
    const dates = dated.map(v => v.date);
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Déterminer l'intervalle approprié en fonction de l'écart de dates
    const daysDiff = differenceInDays(maxDate, minDate);

    let timeLabels = [];
    let periodType = '';

    if (daysDiff <= 30) {
      // Intervalle journalier pour moins d'un mois
      timeLabels = eachDayOfInterval({ start: minDate, end: maxDate }).map(
        date => format(date, 'dd/MM', { locale: fr })
      );
      periodType = 'daily';
    } else if (daysDiff <= 90) {
      // Intervalle hebdomadaire pour 1-3 mois
      timeLabels = eachWeekOfInterval({ start: minDate, end: maxDate }, { locale: fr }).map(
        date => `Sem. ${format(date, 'w', { locale: fr })}`
      );
      periodType = 'weekly';
    } else {
      // Intervalle mensuel pour plus de 3 mois
      timeLabels = eachMonthOfInterval({ start: minDate, end: maxDate }).map(
        date => format(date, 'MMM yyyy', { locale: fr })
      );
      periodType = 'monthly';
    }

    const periodData = timeLabels.map(() => ({ total: 0, count: 0 }));

    dated.forEach(visit => {
      let index;
      if (periodType === 'daily') {
        index = timeLabels.findIndex(label => {
          const labelDate = new Date(minDate);
          labelDate.setDate(parseInt(label.split('/')[0]));
          labelDate.setMonth(parseInt(label.split('/')[1]) - 1);
          return isSameDay(visit.date, labelDate);
        });
      } else if (periodType === 'weekly') {
        index = timeLabels.findIndex((_, i) => {
          const weekStart = eachWeekOfInterval({ start: minDate, end: maxDate }, { locale: fr })[i];
          return isSameWeek(visit.date, weekStart, { locale: fr });
        });
      } else {
        index = timeLabels.findIndex((_, i) => {
          const monthStart = eachMonthOfInterval({ start: minDate, end: maxDate })[i];
          return isSameMonth(visit.date, monthStart);
        });
      }

      if (index >= 0) {
        periodData[index].total += visit.durationMinutes;
        periodData[index].count += 1;
      }
    });

    const avgDurations = periodData.map(data =>
      data.count > 0 ? Math.round(data.total / data.count) : 0
    );

    // 3. Histogramme des durées
    const durationRanges = [0, 16, 31, 46, 61, 91, 121]; // Limites des intervalles
    const durationCounts = Array(7).fill(0);

    dated.forEach(visit => {
      const duration = visit.durationMinutes;
      let rangeIndex = 6; // Par défaut, supposons > 120

      for (let i = 0; i < durationRanges.length - 1; i++) {
        if (duration >= durationRanges[i] && duration < durationRanges[i + 1]) {
          rangeIndex = i;
          break;
        }
      }

      durationCounts[rangeIndex]++;
    });

    return {
      durationPerStore: {
        labels: storeLabels,
        datasets: [{
          label: 'Durée moyenne (min)',
          data: storeDurations,
          backgroundColor: '#3498db',
        }]
      },
      durationTrend: {
        labels: timeLabels,
        datasets: [{
          label: 'Durée moyenne (min)',
          data: avgDurations,
          borderColor: '#2ecc71',
          backgroundColor: 'rgba(46, 204, 113, 0.1)',
          tension: 0.4,
          fill: true,
        }]
      },
      durationHistogram: {
        labels: ['0-15 min', '16-30 min', '31-45 min', '46-60 min', '61-90 min', '91-120 min', '> 120 min'],
        datasets: [{
          label: 'Nombre de visites',
          data: durationCounts,
          backgroundColor: '#9b59b6',
        }]
      }
    };
  }, [dated]);

  // Options communes pour les graphiques
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
        },
        grid: {
          color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        }
      },
      x: {
        ticks: {
          color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
        },
        grid: {
          color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        }
      }
    },
    plugins: {
      legend: {
        labels: {
          color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw || 0;
            if (context.dataset.label.includes('moyenne')) {
              return `${context.dataset.label}: ${Math.floor(value / 60)}h${value % 60 > 0 ? ` ${value % 60}m` : ''}`;
            }
            return `${context.dataset.label}: ${value}`;
          }
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      {dated.length === 0 ? (
        <div className="text-center p-8 text-gray-500 dark:text-gray-400">
          Aucune visite avec durée enregistrée dans la période sélectionnée.
        </div>
      ) : (
        <>
          {/* Graphique de tendance temporelle */}
          <div className="p-4 rounded-lg border dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 dark:text-white">Évolution des durées de visite</h3>
            <div className="h-64">
              <Line
                data={chartData.durationTrend}
                options={{
                  ...commonOptions,
                  plugins: {
                    ...commonOptions.plugins,
                    title: {
                      display: true,
                      text: 'Durée moyenne des visites au fil du temps',
                      color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Histogramme des durées */}
          <div className="p-4 rounded-lg border dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 dark:text-white">Distribution des durées de visite</h3>
            <div className="h-64">
              <Bar
                data={chartData.durationHistogram}
                options={{
                  ...commonOptions,
                  plugins: {
                    ...commonOptions.plugins,
                    title: {
                      display: true,
                      text: 'Nombre de visites par tranche de durée',
                      color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Durée par enseigne */}
          <div className="p-4 rounded-lg border dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 dark:text-white">Durée moyenne par enseigne</h3>
            <div className="h-64">
              <Bar
                data={chartData.durationPerStore}
                options={{
                  ...commonOptions,
                  plugins: {
                    ...commonOptions.plugins,
                    title: {
                      display: true,
                      text: 'Durée moyenne des visites par enseigne',
                      color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    }
                  }
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default VisitsDurationChart;